import React, { useEffect, useState, useRef, useCallback } from "react";
import "./KdsCompletedModal.css";
import { Box, CircularProgress, Modal } from "@mui/material";
import { colorConstant } from "../../../../../styles/colorConstant/colorConstant";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import PosCustomFilterV3 from "../../../../../customComponents/PosCustomFilterV3/PosCustomFilterV3";
import KdsCompletedCard from "../KdsCompletedCard/KdsCompletedCard";
import OdsDateFilter from "../../../delivery/components/OdsDateFilter/OdsDateFilter";
import { filterJobs, updateJobs, CreateRestaurantOrder } from "../../../../../config/Services";
import { getUtcDayRange } from "../../../../../utils/jobDateRange";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import usePrint from "../../../product/hooks/usePrint";
import { assertKitchenResponse } from "../../kitchenActions";

const style = {
  position: "absolute",
  top: 0,
  right: 0,
  width: "80vw",
  maxWidth: "980px",
  height: "100%",
  bgcolor: `${colorConstant.surfaceWhite}`,
  borderTopLeftRadius: "24px",
  borderBottomLeftRadius: "24px",
  borderLeft: "1px solid rgba(226, 232, 240, 0.8)",
  boxShadow: "0 28px 90px rgba(15, 23, 42, 0.22)",
  display: "flex",
  flexDirection: "column",
  outline: "none",
  boxSizing: "border-box",
};

const KdsCompletedModal = ({ openCompletedModal, setOpenCompletedModal, onClose, onOrderReversed }) => {
  const [completedData, setCompletedData] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [completedApiLoader, setCompletedApiLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [masonryColumns, setMasonryColumns] = useState(3);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // This drawer's date is independent of the main KDS filter; null means today.
  const [selectedDate, setSelectedDate] = useState(null);

  const showSnackbar = useSnackbar();
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const requestGeneration = useRef(0);
  const appendInFlight = useRef(false);

  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));
  const appConfigData = localStorage.getItem("appConfig");
  const appConfig = appConfigData ? JSON.parse(appConfigData) : {};
  const sessionId = localStorage.getItem("sessionId");
  const licenseId = localStorage.getItem("licenseId");

  const { startPrint } = usePrint();

  const handleReverseOrderAction = (jobId) => {
    if (!appConfig?.enabledFeatures?.REVERSE_COMPLETED_ORDERS) {
      showSnackbar("Reverse completed orders feature is disabled in POS configurations", "error");
      return Promise.reject();
    }
    const payload = {
      jobIds: [jobId] || [],
      status: "IN_PROGRESS",
      locationId: locId,
      updatedBy: retailUserLocalStorageData?.uidx,
      updatedByName: retailUserLocalStorageData?.firstName,
    };
    return updateJobs(payload)
      .then(async (res) => {
        assertKitchenResponse(res);
        showSnackbar("Order Reversed Successfully", "success");
        setCurrentPage(1);
        setHasMore(true);
        fetchCompletedJobs(1, false);
        await onOrderReversed?.(jobId);
      })
      .catch((err) => {
        showSnackbar("Unable to reverse the order", "error");
        throw err;
      });
  };

  const handleReprintBillAction = (cartId, cartStatus) => {
    if (cartStatus === "CLOSED") {
      showSnackbar("Unable to reprint the Bill as the cart is closed", "warning");
      return Promise.resolve();
    }
    const billPayload = {
      sessionId: sessionId,
      licenseId: licenseId,
      cartId: cartId,
      paymentType: "SPLIT",
      paymentMethod: "CASH",
      paymentMode: "OFFLINE",
      inventoryCheck: "NO",
      amount: 0,
      tender: 0,
      balance: 0,
    };

    return CreateRestaurantOrder(billPayload)
      .then((response) => {
        const orderId = response?.data?.data?.orderResponseModel?.orderId;
        if (orderId) {
          return new Promise((resolve, reject) => {
            startPrint({
              orderId: orderId,
              reprint: true,
              onPrintComplete: () => {
                showSnackbar("Bill printed successfully", "success");
                resolve();
              },
              onPrintError: () => {
                showSnackbar("Failed to print bill", "error");
                reject(new Error("Failed to print bill"));
              },
            });
          });
        } else {
          showSnackbar("Failed to create bill order", "error");
          return Promise.reject(new Error("Failed to create bill order"));
        }
      })
      .catch((error) => {
        showSnackbar("Something went wrong while reprinting bill", "error");
        return Promise.reject(error);
      });
  };

  // Responsive masonry columns observer
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width <= 768) setMasonryColumns(2);
      else setMasonryColumns(3);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // 500ms debounce delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset pagination when filters or search change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setCompletedData([]);
  }, [selectedSources, debouncedSearch, selectedDate]);

  const handleCompletedModalClose = () => {
    requestGeneration.current += 1;
    appendInFlight.current = false;
    setOpenCompletedModal(false);
    setCurrentPage(1);
    setCompletedData([]);
    setHasMore(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setSelectedSources([]);
    setSelectedDate(null);
    setCompletedApiLoader(false);
    setIsLoadingMore(false);
    onClose();
  };

  const fetchCompletedJobs = useCallback(
    (page = 1, append = false) => {
      if (append && appendInFlight.current) return;
      const generation = append ? requestGeneration.current : ++requestGeneration.current;
      if (!append) {
        appendInFlight.current = false;
        setCompletedApiLoader(true);
        setIsLoadingMore(false);
      } else {
        appendInFlight.current = true;
        setIsLoadingMore(true);
      }

      const payload = {
        orgId,
        locationId: locId,
        page,
        pageSize: 10,
        statuses: ["COMPLETED"],
        sortByUpdatedDate: "DESCENDING",
        ...getUtcDayRange(selectedDate),
        ...(selectedSources?.length > 0 && { source: selectedSources?.flat() }),
        ...(debouncedSearch?.trim() && { query: debouncedSearch.trim() }),
      };

      filterJobs(payload)
        .then((res) => {
          if (generation !== requestGeneration.current) return;
          const newData = res?.data?.results || [];

          if (append) {
            setCompletedData((prev) => [...prev, ...newData]);
          } else {
            setCompletedData(newData);
          }

          // Check if there's more data to load
          setHasMore(newData.length === 10);

          appendInFlight.current = false;
          setCompletedApiLoader(false);
          setIsLoadingMore(false);
        })
        .catch((err) => {
          if (generation !== requestGeneration.current) return;
          appendInFlight.current = false;
          showSnackbar("Unable to fetch the completed orders", "error");
          setCompletedApiLoader(false);
          setIsLoadingMore(false);
        });
    },
    [orgId, locId, selectedSources, debouncedSearch, selectedDate]
  );

  // Handle vertical scroll
  const handleScroll = (e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Check if scrolled near the bottom (within 100px)
    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !completedApiLoader && !isLoadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchCompletedJobs(nextPage, true);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Fetch data when modal opens or filters/search change
  useEffect(() => {
    if (openCompletedModal) {
      fetchCompletedJobs(1, false);
    }
    return () => { requestGeneration.current += 1; };
  }, [openCompletedModal, selectedSources, debouncedSearch, selectedDate, fetchCompletedJobs]);

  // Empty State Component
  const EmptyState = () => {
    // A date only counts as an active filter when it isn't today — today is the default view
    const isNonTodayDate =
      !!selectedDate && selectedDate.toDateString() !== new Date().toDateString();
    const isSearchOrFilterActive =
      debouncedSearch?.trim() || selectedSources?.length > 0 || isNonTodayDate;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          gap: "16px",
          color: "#6B7280",
          width: "100%",
        }}
      >
        <div
          style={{
            width: "80px",
            height: "80px",
            backgroundColor: "#F3F4F6",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <div style={{ textAlign: "center" }}>
          <p
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#374151",
              margin: "0 0 8px 0",
            }}
          >
            {isSearchOrFilterActive ? "No matching orders found" : "No completed orders"}
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#6B7280",
              margin: 0,
            }}
          >
            {isSearchOrFilterActive
              ? "Try adjusting your search or filter criteria"
              : "Completed orders will appear here"}
          </p>
        </div>
        {isSearchOrFilterActive && (
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedSources([]);
              setSelectedDate(null);
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#2A71FF",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
              marginTop: "8px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#1d5fd6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#2A71FF";
            }}
          >
            Clear filters
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      <Modal
        open={openCompletedModal}
        onClose={handleCompletedModalClose}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
      >
        <Box sx={style}>
          <div className="kds-completed-header">
            <div className="kds-completed-header-left">
              <p className="kds-completed-header-text">Completed Items</p>
              <div className="kds-completed-order-count">
                <p>{completedData?.length}</p>
              </div>
            </div>
            <button className="kds-completed-close-btn" aria-label="Close completed orders" onClick={handleCompletedModalClose}>
              <PosCustomIcon icon={<XMarkIcon />} height={"18px"} width={"18px"} color="#64748b" />
            </button>
          </div>

          <div className="kds-comp-search-container">
            <div className="kds-input-container" style={{ position: "relative" }}>
              <PosCustomIcon
                icon={<MagnifyingGlassIcon />}
                color={"#535861"}
                height={"1rem"}
                width={"1rem"}
              />
              <input
                type="text"
                placeholder="Search items"
                className="kds-search-bar"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searchQuery && (
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "background-color 0.2s",
                  }}
                  onClick={handleClearSearch}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#F3F4F6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <PosCustomIcon
                    icon={<XMarkIcon />}
                    color={"#6B7280"}
                    height={"16px"}
                    width={"16px"}
                  />
                </div>
              )}
            </div>
            <div className="kds-comp-date-container">
              <OdsDateFilter selectedDate={selectedDate} onChange={(d) => setSelectedDate(d)} />
            </div>
            <div className="kds-comp-fiter-container">
              <PosCustomFilterV3
                options={[
                  { id: 1, label: "Zomato", value: "ZOMATO_ORDER" },
                  { id: 2, label: "Swiggy", value: "SWIGGY_ORDER" },
                  { id: 3, label: "Eat Sure", value: "EATSURE_ORDER" },
                  { id: 4, label: "POS", value: ["PALLET_POS", "PALLET_MPOS"] },
                  { id: 5, label: "Whatsapp", value: "WHATSAPP_ORDER" },
                  { id: 6, label: "Website", value: ["B2C_ORDER", "RESTAURANT_ORDER"] },
                ]}
                selectedValues={selectedSources}
                onChange={(filter) => {
                  setSelectedSources(filter);
                }}
                styleProps={{
                  buttonLabel: "Filter By Source",
                  buttonLabelColor: "#475569",
                  buttonLabelFontSize: "14px",
                  buttonLabelFontWeight: "500",
                  buttonRadius: "50px",
                  buttonShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  buttonBorder: "1px solid #e2e8f0",
                  badgeBg: "#2A71FF",
                  badgeTextColor: "#ffffff",
                  badgeSize: "18px",
                  badgePosition: "top-right",
                  checkboxShape: "square",
                  checkboxBorderRadius: "4px",
                  checkboxBgSelected: "#2A71FF",
                  checkboxBorderColorSelected: "#2A71FF",
                  checkboxTickColor: "#ffffff",
                  iconColor: "#94a3b8",
                  buttonWidth: "100%",
                  buttonPadding: "12px 16px",
                }}
              />
            </div>
          </div>

          {completedApiLoader ? (
            <div className="KdsCompLoaderContainer">
              <CircularProgress size={"24px"} sx={{ color: "#0860E6" }} />
            </div>
          ) : (
            <div className="kds-completed-card-board" ref={containerRef} onScroll={handleScroll}>
              {completedData?.length > 0 ? (
                <>
                  {(() => {
                    const columns = Array.from({ length: masonryColumns }, () => []);
                    let cardIndex = 0;

                    (completedData || []).forEach((item) => {
                      columns[cardIndex % masonryColumns].push(item);
                      cardIndex++;
                    });

                    return (
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          width: "100%",
                          alignItems: "flex-start",
                        }}
                      >
                        {columns?.map((col, colIndex) => (
                          <div
                            key={colIndex}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "16px",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {col?.map((item, idx) => (
                              <KdsCompletedCard
                                mode="completed"
                                prepTime={item?.metadata?.avgPrepTime ?? "N/A"}
                                key={item?.jobId || idx}
                                tokenNumber={item?.token}
                                source={item?.source}
                                orderType={item?.type}
                                subOrderType={item?.metadata?.subOrderType}
                                cartId={item?.metadata?.cartId}
                                user={item?.createdByName}
                                items={item?.tasks}
                                jobId={item?.jobId}
                                updatedTime={item?.updated}
                                onReverseOrder={handleReverseOrderAction}
                                onReprintBill={(cartId) =>
                                  handleReprintBillAction(cartId, item?.metadata?.cartStatus)
                                }
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Loading indicator at the end */}
                  {isLoadingMore && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px 0",
                        width: "100%",
                      }}
                    >
                      <CircularProgress size={"24px"} sx={{ color: "#0860E6" }} />
                    </div>
                  )}
                </>
              ) : (
                <EmptyState />
              )}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default KdsCompletedModal;
